import React, { useState, useEffect, useRef } from 'react';
import CssEditor from './CssEditor';
import { GetRequest, PostRequest } from 'components/common/index.js';

const CssEditorModal = ({ isOpen, onClose, onSave, currentCss, theme, domains = [] }) => {
  const [cssContent, setCssContent] = useState(currentCss || '');
  const [defaultCss, setDefaultCss] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const modalRef = useRef(null);

  // Load default CSS when modal opens
  useEffect(() => {
    if (isOpen && theme) {
      loadDefaultCss();
    }
  }, [isOpen, theme]);

  // Set initial CSS content
  useEffect(() => {
    if (isOpen) {
      setCssContent(currentCss || '');
    }
  }, [isOpen, currentCss]);

  const loadDefaultCss = () => {
    setIsLoading(true);
    GetRequest({
      url: `/quote-builder/widgets/default-css/${theme}`,
      callback: (response) => {
        if (response && response.css) {
          setDefaultCss(response.css);
          // If no current CSS, use default
          if (!currentCss) {
            setCssContent(response.css);
          }
        }
        setIsLoading(false);
      },
      404: () => {
        console.error('Failed to load default CSS: not found');
        setIsLoading(false);
      },
      500: () => {
        console.error('Failed to load default CSS: server error');
        setIsLoading(false);
      }
    });
  };

  const handleCssChange = (newCss) => {
    setCssContent(newCss);
    // Clear validation errors on change
    setValidationErrors([]);
  };

  const validateAndSave = () => {
    setIsSaving(true);
    PostRequest({
      url: '/quote-builder/widgets/validate-css',
      data: { css: cssContent, domains: domains },
      callback: (response) => {
        if (response && response.valid) {
          setValidationErrors([]);
          onSave(cssContent);
          onClose();
        } else {
          setValidationErrors(response.errors || ['Invalid CSS']);
        }
        setIsSaving(false);
      },
      400: (response) => {
        setValidationErrors(response.errors || ['Invalid CSS']);
        setIsSaving(false);
      },
      500: () => {
        setValidationErrors(['Failed to validate CSS']);
        setIsSaving(false);
      }
    });
  };

  const handleSave = () => {
    validateAndSave();
  };

  const handleResetToDefault = () => {
    setCssContent(defaultCss);
    setValidationErrors([]);
  };

  const handleClearCss = () => {
    setCssContent('');
    setValidationErrors([]);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="modal"
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={modalRef}
        className="modal-content"
        style={{
          maxWidth: '900px',
          width: '90%',
          margin: '50px auto',
          backgroundColor: '#fff',
          borderRadius: '8px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 style={{ margin: 0, fontSize: '18px' }}>
            {t('quote_builder_widget.edit_custom_css')}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666',
            }}
          >
            &times;
          </button>
        </div>

        {/* Info banner */}
        <div
          style={{
            padding: '12px 20px',
            backgroundColor: '#f0f7ff',
            borderBottom: '1px solid #d0e3ff',
            fontSize: '13px',
            color: '#1a4a7a',
          }}
        >
          {t('quote_builder_widget.css_editor_info')}
        </div>

        {/* Validation errors */}
        {validationErrors.length > 0 && (
          <div
            style={{
              padding: '12px 20px',
              backgroundColor: '#fff0f0',
              borderBottom: '1px solid #ffcccc',
            }}
          >
            {validationErrors.map((error, index) => (
              <div key={index} style={{ color: '#cc0000', fontSize: '13px' }}>
                {error}
              </div>
            ))}
          </div>
        )}

        {/* Editor */}
        <div style={{ flex: 1, padding: '20px', overflow: 'hidden' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              {t('quote_builder_widget.loading')}...
            </div>
          ) : (
            <CssEditor
              initialValue={cssContent}
              onChange={handleCssChange}
              height="400px"
            />
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 20px',
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <button
              onClick={handleResetToDefault}
              className="btn btn-outline-secondary"
              style={{ marginRight: '8px' }}
              disabled={isLoading || isSaving}
            >
              {t('quote_builder_widget.reset_to_default')}
            </button>
            <button
              onClick={handleClearCss}
              className="btn btn-outline-warning"
              disabled={isLoading || isSaving}
            >
              {t('quote_builder_widget.clear_css')}
            </button>
          </div>
          <div>
            <button
              onClick={onClose}
              className="btn btn-secondary"
              style={{ marginRight: '8px' }}
              disabled={isSaving}
            >
              {t('quote_builder_widget.cancel')}
            </button>
            <button
              onClick={handleSave}
              className="btn btn-primary"
              disabled={isLoading || isSaving}
            >
              {isSaving ? t('quote_builder_widget.saving') + '...' : t('quote_builder_widget.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CssEditorModal;
